"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    COUT_EUROS,
    MILLESIME,
    VALIDITE_ANS,
    composerChamps,
    concerne,
    fenetreDeRenouvellement,
    recapitulatif,
    valider,
} from "@/lib/tools/itinerantTraderCard";
import { dateEnToutesLettres, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerFormulaire } from "@/lib/tools/officialFormPdf";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La carte d'activité ambulante, et l'imprimé qui la demande.
 *
 * LE TRI ÉCARTE DEUX PROFILS QUE PERSONNE N'ÉCARTE : celui qui ne vend que dans
 * sa propre commune, à qui la loi ne demande rien s'il a un domicile stable, et
 * les métiers que le règlement écarte, des agents commerciaux aux vendeurs à
 * domicile indépendants. Les deux paient parfois 30 € pour une carte dont ils
 * n'ont pas besoin. À l'inverse, sans domicile stable, la déclaration est due
 * partout : ce cas a son propre choix, pour ne jamais passer par « ma commune ».
 *
 * L'imprimé est rempli par `lib/tools/itinerantTraderCard.js`, qui connaît ses
 * champs et ses pièges.
 */

const L = {
    fr: {
        phases: ["Votre situation", "Votre déclaration", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : l'imprimé s'y remplit, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",

        zoneTitre: "Où exercez-vous votre activité ambulante ?",
        zoneAide: "C'est la question qui décide de l'obligation. Avec un domicile stable, la loi ne vise que l'activité exercée hors de la commune de votre domicile ou de votre établissement principal. Sans domicile stable, elle vise toute activité ambulante, où qu'elle s'exerce.",
        zoneCommune: "Seulement dans ma commune",
        zoneCommuneDetail: "Celle de mon domicile ou de mon établissement",
        zoneHors: "Aussi dans d'autres communes",
        zoneHorsDetail: "Marchés, tournées, salons ailleurs",
        zoneSansDomicile: "Je n'ai pas de domicile stable",
        zoneSansDomicileDetail: "Caravane, camping-car ou aucune habitation fixe",

        dispenseTitre: "Exercez-vous l'une de ces activités ?",
        dispenseAide: "Le code de commerce les écarte de la déclaration préalable, la plupart parce qu'une réglementation propre les encadre déjà. Si vous exercez l'une d'elles, la carte ne vous concerne pas.",
        dispenseAgent: "Agent commercial",
        dispensePresse: "Vendeur-colporteur de presse",
        dispenseTaxi: "Exploitant de taxi",
        dispenseVrp: "Voyageur, représentant ou placier (VRP)",
        dispenseDemarchage: "Démarcheur bancaire ou financier",
        dispenseVdi: "Vendeur à domicile indépendant",
        dispenseTournees: "Tournées accessoires dans les communes voisines",
        dispenseTourneesDetail: "Dans les communes limitrophes, depuis un établissement fixe",
        dispenseAucune: "Aucune de ces activités",

        objetTitre: "Que demandez-vous ?",
        objetAide: "La carte vaut {annees} ans. Le renouvellement se demande entre un mois avant et deux mois après son échéance.",
        objetInitiale: "Une première carte",
        objetRenouvellement: "Un renouvellement",
        objetModification: "Une modification",
        champExpiration: "Date de fin de votre carte actuelle",
        aideExpiration: "Elle figure sur la carte. C'est elle qui ouvre la fenêtre de renouvellement.",
        champAvecCreation: "Faites-vous cette demande en même temps que la création de votre entreprise ?",
        avecCreationOui: "Oui, en même temps",
        avecCreationNon: "Non, mon entreprise existe déjà",
        fenetreOuverte: "Votre fenêtre de renouvellement est ouverte jusqu'au {fermeture}.",
        fenetreTropTot: "Votre fenêtre s'ouvre le {ouverture}. Vous pouvez préparer l'imprimé dès maintenant, mais la chambre ne l'examinera pas avant cette date.",

        horsSujetTitre: "Vous n'avez pas besoin de cette carte",
        refusCommune: "La déclaration n'est exigée que pour exercer hors de la commune où se trouve votre habitation ou votre établissement principal. Tant que vous restez chez vous, marché de votre commune compris, la loi ne vous demande rien et personne ne peut vous réclamer de carte. Le jour où vous irez vendre dans la commune voisine, revenez : la déclaration devra être faite avant de commencer.",
        refusDispense: "Le code de commerce écarte votre activité de la déclaration préalable. Vous n'avez ni carte à demander, ni redevance à payer pour elle. Méfiez-vous des sites qui vendent cette démarche à tout le monde : ils ne posent jamais cette question.",
        refusTropTard: "Votre carte est expirée depuis plus de deux mois, et la fenêtre de renouvellement est fermée. Il faut demander une nouvelle carte, avec le dossier d'une première demande. Recommencez ce parcours en choisissant « une première carte ».",

        identiteTitre: "Qui déclare ?",
        identiteAide: "L'imprimé demande votre état civil complet. Le nom d'usage ne se remplit que si vous en portez un.",
        champNomNaissance: "Nom de naissance",
        champPrenoms: "Prénoms",
        champNomUsage: "Nom d'usage",
        aideNomUsage: "Facultatif. Nom d'épouse ou d'époux, par exemple.",
        champDateNaissance: "Date de naissance",
        champLieuNaissance: "Lieu de naissance",
        aideLieuNaissance: "Commune et département, comme sur votre pièce d'identité.",
        champNationalite: "Nationalité",

        activiteTitre: "Votre activité et votre rattachement",
        activiteAide: "L'imprimé demande votre domicile ou votre commune de rattachement, et l'activité telle qu'elle est déclarée à votre immatriculation.",
        champDomicile: "Domicile ou commune de rattachement",
        champActivite: "Activité commerciale ou artisanale exercée",
        aideActivite: "Reprenez le libellé de votre immatriculation, par exemple « vente de produits de boulangerie sur les marchés ».",

        signatureTitre: "Où et quand signez-vous ?",
        signatureAide: "L'imprimé se signe à la main ou ici. Il part ensuite à la chambre de commerce ou à la chambre des métiers, par lettre recommandée ou déposé contre récépissé.",
        champLieuSignature: "Lieu de signature",
        champDateDeSignature: "Date de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",

        continuer: "Continuer",
        voirDocument: "Voir mon imprimé",
        resultatTitre: "Votre déclaration préalable",
        recapTitre: "Ce que vous déclarez",
        telecharger: "Télécharger l'imprimé rempli",
        erreurTelechargement: "L'imprimé officiel n'a pas pu être chargé. Réessayez dans un instant.",
        recommencer: "Recommencer",

        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre imprimé.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais il y a un problème dans ce qu'ils contiennent. Corrigez-les, puis revenez ici.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurAVenir: "Cette date est dans le futur.",
        erreurTropJeune: "Cette date ferait de vous un mineur. Vérifiez l'année.",
        erreurTropVieux: "Cette date semble trop ancienne. Vérifiez l'année.",
        alerteTropTot: "Votre fenêtre de renouvellement n'est pas encore ouverte. L'imprimé est prêt, mais attendez le {ouverture} pour le déposer.",

        piecesTitre: "Les pièces à joindre",
        piecesAllegees: "Votre demande accompagne la création de votre entreprise : l'imprimé précise que dans ce cas, deux photographies d'identité récentes suffisent.",
        pieces1: "Deux photographies d'identité récentes.",
        pieces2: "Une copie de votre pièce d'identité, ou de votre titre de circulation ou de séjour.",
        pieces3: "Votre justificatif d'immatriculation au répertoire des entreprises et des établissements.",
        piecesRenouvellement: "Pour un renouvellement, ajoutez un extrait de moins de trois mois de votre inscription au registre.",

        suiteTitre: "Et maintenant",
        suite1: "Imprimez la déclaration, signez-la, et adressez-la à la chambre de commerce ou à la chambre des métiers dont vous dépendez, par lettre recommandée avec accusé de réception ou en main propre contre récépissé.",
        suite2: "Prévoyez la redevance de {cout} € : la carte n'est pas délivrée sans elle.",
        suite3: "Comptez quinze jours à un mois pour la recevoir. Si vous devez commencer avant, demandez le certificat provisoire, valable un mois.",
        suite4: "Gardez la carte sur vous : elle se présente à toute réquisition des agents, avec une pièce d'identité.",

        fondTitre: "Ce que vous risquez sans elle",
        fond: "Exercer une activité ambulante sans avoir fait la déclaration préalable est puni d'une amende de 750 €. Ne pas avoir sa carte sur soi, ou ne pas l'avoir renouvelée, coûte 450 €. La carte se renouvelle tous les {annees} ans, et personne ne vous préviendra de son échéance.",
    },
    en: {
        phases: ["Your situation", "Your declaration", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device: the official form is filled in there, nobody receives your answers, not even us, and it all disappears with the tab.",

        zoneTitre: "Where do you carry out your itinerant activity?",
        zoneAide: "This is the question that decides the obligation. With a stable home, the law only covers activity carried out outside the town of your home or your main establishment. With no stable home, it covers any itinerant activity, wherever it takes place.",
        zoneCommune: "Only in my own town",
        zoneCommuneDetail: "Where I live or where my business is",
        zoneHors: "In other towns as well",
        zoneHorsDetail: "Markets, rounds, fairs elsewhere",
        zoneSansDomicile: "I have no stable home",
        zoneSansDomicileDetail: "Caravan, motorhome or no fixed dwelling",

        dispenseTitre: "Do you carry out one of these activities?",
        dispenseAide: "The commercial code keeps them out of the prior declaration, most of them because rules of their own already govern them. If you carry out one of them, the card does not concern you.",
        dispenseAgent: "Commercial agent",
        dispensePresse: "Newspaper street vendor",
        dispenseTaxi: "Taxi operator",
        dispenseVrp: "Sales representative (VRP)",
        dispenseDemarchage: "Banking or financial canvasser",
        dispenseVdi: "Independent home-sales representative",
        dispenseTournees: "Incidental rounds in neighbouring towns",
        dispenseTourneesDetail: "In bordering towns, from a fixed establishment",
        dispenseAucune: "None of these",

        objetTitre: "What are you applying for?",
        objetAide: "The card lasts {annees} years. Renewal is requested between one month before and two months after it expires.",
        objetInitiale: "A first card",
        objetRenouvellement: "A renewal",
        objetModification: "A change",
        champExpiration: "Expiry date of your current card",
        aideExpiration: "It is printed on the card. It is what opens the renewal window.",
        champAvecCreation: "Are you applying at the same time as registering your business?",
        avecCreationOui: "Yes, at the same time",
        avecCreationNon: "No, my business already exists",
        fenetreOuverte: "Your renewal window is open until {fermeture}.",
        fenetreTropTot: "Your window opens on {ouverture}. You can prepare the form now, but the chamber will not examine it before that date.",

        horsSujetTitre: "You do not need this card",
        refusCommune: "You only have to declare if you trade outside the town where your home or main establishment is. As long as you stay there, your own town's market included, the law asks nothing of you and nobody can demand a card. The day you go and sell in the next town, come back: the declaration has to be made before you start.",
        refusDispense: "The commercial code keeps your activity out of the prior declaration. You have no card to apply for and no fee to pay for it. Be wary of websites that sell this procedure to everyone: they never ask this question.",
        refusTropTard: "Your card expired more than two months ago, and the renewal window has closed. You need to apply for a new card, with the file of a first application. Start this path again and choose « a first card ».",

        identiteTitre: "Who is declaring?",
        identiteAide: "The form asks for your full civil status. The used name is only filled in if you have one.",
        champNomNaissance: "Birth name",
        champPrenoms: "Forenames",
        champNomUsage: "Used name",
        aideNomUsage: "Optional. A married name, for instance.",
        champDateNaissance: "Date of birth",
        champLieuNaissance: "Place of birth",
        aideLieuNaissance: "Town and department, as on your identity document.",
        champNationalite: "Nationality",

        activiteTitre: "Your activity and where you are attached",
        activiteAide: "The form asks for your home or town of attachment, and the activity as declared on your registration.",
        champDomicile: "Home or town of attachment",
        champActivite: "Commercial or craft activity carried out",
        aideActivite: "Use the wording from your registration, for example « selling bakery products at markets ».",

        signatureTitre: "Where and when are you signing?",
        signatureAide: "The form is signed by hand or here. It then goes to the chamber of commerce or the chamber of trades, by registered post or delivered against a receipt.",
        champLieuSignature: "Place of signature",
        champDateDeSignature: "Date of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",

        continuer: "Continue",
        voirDocument: "See my form",
        resultatTitre: "Your prior declaration",
        recapTitre: "What you are declaring",
        telecharger: "Download the completed form",
        erreurTelechargement: "The official form could not be loaded. Try again in a moment.",
        recommencer: "Start again",

        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your form.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but there is a problem with what they contain. Correct them, then come back here.",
        erreurFormat: "This format is not recognised.",
        erreurAVenir: "That date is in the future.",
        erreurTropJeune: "That date would make you a minor. Check the year.",
        erreurTropVieux: "That date looks too far back. Check the year.",
        alerteTropTot: "Your renewal window is not open yet. The form is ready, but wait until {ouverture} to file it.",

        piecesTitre: "The documents to attach",
        piecesAllegees: "Your application goes with the registration of your business: the form states that in that case, two recent passport photographs are enough.",
        pieces1: "Two recent passport photographs.",
        pieces2: "A copy of your identity document, or of your travel or residence permit.",
        pieces3: "Your certificate of registration in the national business register.",
        piecesRenouvellement: "For a renewal, add an extract of your registration less than three months old.",

        suiteTitre: "What happens next",
        suite1: "Print the declaration, sign it, and send it to the chamber of commerce or the chamber of trades you depend on, by registered post with acknowledgement of receipt or in person against a receipt.",
        suite2: "Budget the {cout} € fee: the card is not issued without it.",
        suite3: "Allow fifteen days to a month to receive it. If you have to start before then, ask for the provisional certificate, valid for one month.",
        suite4: "Keep the card on you: it must be shown to officers on request, together with an identity document.",

        fondTitre: "What you risk without it",
        fond: "Carrying out an itinerant activity without having made the prior declaration is punished by a €750 fine. Not having your card on you, or not having renewed it, costs €450. The card is renewed every {annees} years, and nobody will remind you of the date.",
    },
};

const LIBELLES_MANQUES = {
    fr: {
        zone: "Votre zone d'activité",
        nomNaissance: "Votre nom de naissance",
        prenoms: "Vos prénoms",
        nationalite: "Votre nationalité",
        domicile: "Votre domicile ou commune de rattachement",
        activite: "L'activité exercée",
        lieuNaissance: "Votre lieu de naissance",
        dateNaissance: "Votre date de naissance",
        expiration: "La date de fin de votre carte",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de signature",
    },
    en: {
        zone: "Where you trade",
        nomNaissance: "Your birth name",
        prenoms: "Your forenames",
        nationalite: "Your nationality",
        domicile: "Your home or town of attachment",
        activite: "The activity carried out",
        lieuNaissance: "Your place of birth",
        dateNaissance: "Your date of birth",
        expiration: "Your card's expiry date",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of signature",
    },
};

const PHASES_PAR_ECRAN = {
    zone: 0,
    dispense: 0,
    objet: 0,
    horsSujet: 0,
    identite: 1,
    activite: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    zone: "",
    dispense: "",
    objet: "",
    expiration: "",
    avecCreation: "",
    nomNaissance: "",
    prenoms: "",
    nomUsage: "",
    dateNaissance: "",
    lieuNaissance: "",
    nationalite: "Française",
    domicile: "",
    activite: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

export default function CarteCommercantAmbulant({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [erreurTelechargement, setErreurTelechargement] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "zone",
        () => {
            setR({ ...VIDE, ...identite });
            setErreurTelechargement(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    useSignalerIdentite(
        {
            nomNaissance: r.nomNaissance,
            nomUsage: r.nomUsage,
            prenoms: r.prenoms,
            dateNaissance: r.dateNaissance,
            nationalite: r.nationalite,
        },
        etape === "activite" || etape === "signature" || etape === "resultat",
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour, aujourdhui }),
        [r, jour, aujourdhui],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const champs = useMemo(() => composerChamps(reponses, aujourdhui), [reponses, aujourdhui]);
    const recap = useMemo(() => recapitulatif(reponses, aujourdhui), [reponses, aujourdhui]);
    const fenetre = useMemo(
        () => (r.expiration ? fenetreDeRenouvellement(r.expiration, aujourdhui) : null),
        [r.expiration, aujourdhui],
    );

    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());
    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (refus.motif === "aVenir") return t.erreurAVenir;
        if (refus.motif === "tropJeune") return t.erreurTropJeune;
        if (refus.motif === "tropVieux") return t.erreurTropVieux;
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(reponses);

    const telecharger = async () => {
        setErreurTelechargement(false);
        try {
            await telechargerFormulaire({
                champs,
                nomFichier: nomDuFichier(`declaration-ambulante-${MILLESIME.numero.replace("*", "-")}`, {
                    prenoms: r.prenoms,
                    nom: r.nomNaissance,
                }),
                signature,
            });
        } catch {
            // L'imprimé vierge n'a pas pu être chargé : le dire, plutôt que de
            // laisser un bouton qui ne fait rien.
            setErreurTelechargement(true);
        }
    };

    /* ---------------------------------------------------------------- */
    /* Le tri                                                            */
    /* ---------------------------------------------------------------- */

    if (etape === "zone") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.zoneTitre} aide={t.zoneAide}>
                <div className="grid sm:grid-cols-3 gap-3">
                    <Choix
                        icone="home-linear"
                        titre={t.zoneCommune}
                        detail={t.zoneCommuneDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, zone: "maCommune" }));
                            aller("horsSujet");
                        }}
                    />
                    <Choix
                        icone="map-point-linear"
                        titre={t.zoneHors}
                        detail={t.zoneHorsDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, zone: "horsCommune" }));
                            aller("dispense");
                        }}
                    />
                    <Choix
                        icone="map-arrow-right-linear"
                        titre={t.zoneSansDomicile}
                        detail={t.zoneSansDomicileDetail}
                        onClick={() => {
                            // Sans domicile stable, aucune commune n'échappe à la
                            // déclaration : seules les dispenses restent à voir.
                            setR((p) => ({ ...p, zone: "sansDomicileStable" }));
                            aller("dispense");
                        }}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "dispense") {
        const cas = [
            { cle: "agentCommercial", icone: "case-round-linear", titre: t.dispenseAgent },
            { cle: "colporteurPresse", icone: "notebook-linear", titre: t.dispensePresse },
            { cle: "taxi", icone: "bus-linear", titre: t.dispenseTaxi },
            { cle: "vrp", icone: "user-id-linear", titre: t.dispenseVrp },
            { cle: "demarchageFinancier", icone: "wallet-money-linear", titre: t.dispenseDemarchage },
            { cle: "vendeurADomicile", icone: "home-2-linear", titre: t.dispenseVdi },
            {
                cle: "tourneesLimitrophes",
                icone: "routing-linear",
                titre: t.dispenseTournees,
                detail: t.dispenseTourneesDetail,
            },
            { cle: "aucune", icone: "close-circle-linear", titre: t.dispenseAucune },
        ];
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.dispenseTitre} aide={t.dispenseAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    {cas.map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, dispense: c.cle }));
                                aller(c.cle === "aucune" ? "objet" : "horsSujet");
                            }}
                        />
                    ))}
                </div>
            </Ecran>
        );
    }

    if (etape === "objet") {
        const renouvelle = r.objet === "renouvellement";
        const initiale = r.objet === "initiale";
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.objetTitre}
                aide={remplir(t.objetAide, { annees: VALIDITE_ANS })}
            >
                <div className="space-y-5">
                    <Segments
                        options={[
                            { cle: "initiale", libelle: t.objetInitiale },
                            { cle: "renouvellement", libelle: t.objetRenouvellement },
                            { cle: "modification", libelle: t.objetModification },
                        ]}
                        valeur={r.objet}
                        poser={poser("objet")}
                    />
                    {renouvelle && (
                        <Champ
                            type="date"
                            libelle={t.champExpiration}
                            aide={t.aideExpiration}
                            valeur={r.expiration}
                            poser={poser("expiration")}
                            erreur={erreurDe("expiration")}
                        />
                    )}
                    {renouvelle && fenetre && fenetre.etat !== "tropTard" && (
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {fenetre.etat === "ouverte"
                                ? remplir(t.fenetreOuverte, {
                                      fermeture: dateEnToutesLettres(fenetre.fermeture),
                                  })
                                : remplir(t.fenetreTropTot, {
                                      ouverture: dateEnToutesLettres(fenetre.ouverture),
                                  })}
                        </p>
                    )}
                    {initiale && (
                        <div>
                            <Segments
                                libelle={t.champAvecCreation}
                                options={[
                                    { cle: "oui", libelle: t.avecCreationOui },
                                    { cle: "non", libelle: t.avecCreationNon },
                                ]}
                                valeur={r.avecCreation}
                                poser={poser("avecCreation")}
                            />
                        </div>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !r.objet ||
                        (initiale && !r.avecCreation) ||
                        (renouvelle && (!rempli("expiration") || Boolean(refusDe("expiration"))))
                    }
                    onClick={() => aller(tri.motif === "renouvellementTropTard" ? "horsSujet" : "identite")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {tri.motif === "dansMaCommune" && t.refusCommune}
                        {tri.motif === "dispense" && t.refusDispense}
                        {tri.motif === "renouvellementTropTard" && t.refusTropTard}
                    </p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    /* ---------------------------------------------------------------- */
    /* La saisie                                                         */
    /* ---------------------------------------------------------------- */

    if (etape === "identite") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.identiteTitre} aide={t.identiteAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champNomNaissance} valeur={r.nomNaissance} poser={poser("nomNaissance")} />
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                    </div>
                    <Champ
                        libelle={t.champNomUsage}
                        aide={t.aideNomUsage}
                        valeur={r.nomUsage}
                        poser={poser("nomUsage")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ
                            type="date"
                            libelle={t.champDateNaissance}
                            valeur={r.dateNaissance}
                            poser={poser("dateNaissance")}
                            erreur={erreurDe("dateNaissance")}
                        />
                        <Champ
                            libelle={t.champLieuNaissance}
                            aide={t.aideLieuNaissance}
                            valeur={r.lieuNaissance}
                            poser={poser("lieuNaissance")}
                        />
                    </div>
                    <Champ libelle={t.champNationalite} valeur={r.nationalite} poser={poser("nationalite")} />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("nomNaissance", "prenoms", "dateNaissance", "lieuNaissance", "nationalite") ||
                        Boolean(refusDe("dateNaissance"))
                    }
                    onClick={() => aller("activite")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "activite") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.activiteTitre} aide={t.activiteAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champDomicile} valeur={r.domicile} poser={poser("domicile")} />
                    <Champ
                        libelle={t.champActivite}
                        aide={t.aideActivite}
                        valeur={r.activite}
                        poser={poser("activite")}
                    />
                </div>
                <Avancer pleineLargeur disabled={!rempli("domicile", "activite")} onClick={() => aller("signature")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.signatureTitre} aide={t.signatureAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateDeSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer pleineLargeur disabled={!rempli("lieuDeSignature")} onClick={() => aller("resultat")}>
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    const pieces = controle.alertes.includes("piecesAllegees")
        ? [t.pieces1]
        : [t.pieces1, t.pieces2, t.pieces3].concat(
              r.objet === "renouvellement" ? [t.piecesRenouvellement] : [],
          );

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!champs ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurDe(champ)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.includes("renouvellementTropTot") && fenetre && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteTropTot, {
                                    ouverture: dateEnToutesLettres(fenetre.ouverture),
                                })}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                            {titre}
                                        </td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <button
                        type="button"
                        onClick={telecharger}
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                    >
                        <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                        {t.telecharger}
                    </button>
                    {erreurTelechargement && (
                        <p className="text-sm text-amber-600 dark:text-amber-400 mt-3 mb-0">
                            {t.erreurTelechargement}
                        </p>
                    )}

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-1">
                            <SolarIcon
                                name="paperclip-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.piecesTitre}</div>
                        </div>
                        {controle.alertes.includes("piecesAllegees") && (
                            <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mt-2 mb-0">
                                {t.piecesAllegees}
                            </p>
                        )}
                        <Liste items={pieces} />
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <ul className="space-y-3">
                            {[t.suite1, remplir(t.suite2, { cout: COUT_EUROS }), t.suite3, t.suite4].map((ligne) => (
                                <li
                                    key={ligne}
                                    className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                                >
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="shield-warning-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {remplir(t.fond, { annees: VALIDITE_ANS })}
                        </p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
